// The authenticated backend chooses the linked wallet. Never send provider keys
// or accept a browser-selected wallet as ownership authority.
export async function fetchInventorySnapshot(get, {chain, page = 1, pageSize = 100, forceRefresh = false, isCurrent = () => true, onSnapshot = () => {}}) {
      const items = [];
      let snapshot, snapshotMode;
      let currentPage = Math.max(1, page), expectedTotal;
      const effectivePageSize = Math.min(100, Math.max(1, pageSize));
      const seenPages = new Set();
      for (;;) {
        const response = await get("/nfts", {
          params: { snapshot: true, chain, page: currentPage, pageSize: effectivePageSize,
            forceRefresh: forceRefresh && currentPage === Math.max(1, page) ? "true" : "false" },
        });
        if (!isCurrent()) return null;
        const payload = Array.isArray(response.data) ? response.data : response.data?.data;
        const hasSnapshot = !!payload && !Array.isArray(payload) && Object.hasOwn(payload, 'snapshot');
        if (snapshotMode !== undefined && snapshotMode !== hasSnapshot) throw new Error("NFT inventory changed during refresh. Please retry.");
        snapshotMode = hasSnapshot;
        if (hasSnapshot) {
          const meta = payload.snapshot;
          if (!meta || typeof meta.revision !== 'string' || !/^[a-f0-9]{32}$/.test(meta.revision) ||
              typeof meta.walletAddress !== 'string' || typeof meta.syncedAt !== 'string' ||
              (meta.syncedAt && !Number.isFinite(Date.parse(meta.syncedAt)))) throw new Error("Invalid NFT snapshot metadata");
          const next = {revision:meta.revision,walletAddress:meta.walletAddress,syncedAt:meta.syncedAt};
          if (snapshot && JSON.stringify(snapshot) !== JSON.stringify(next)) throw new Error("NFT inventory changed during refresh. Please retry.");
          snapshot = next;
        }
        const batch = hasSnapshot ? payload.items : payload;
        if (!Array.isArray(batch)) throw new Error("Invalid NFT inventory response");
        items.push(...batch);
        const pagination = hasSnapshot ? payload.pagination : response.data?.pagination;
        if (hasSnapshot && !pagination) throw new Error("Missing NFT snapshot pagination");
        if (!pagination) break; // Legacy unpaginated response.
        // Deployed tsj v0.0.8 gateway keeps only {total}; the newer service's
        // totalCount is lost and becomes total:0. Walk until a short page rather
        // than trusting that zero as proof of an empty wallet.
        if (pagination.totalPages === undefined && Number.isInteger(pagination.total) && pagination.total >= 0) {
          if (batch.length < effectivePageSize) break;
          const signature = JSON.stringify(batch.map(item => [item.chain, item.token_address, item.token_id ?? item.id]));
          if (seenPages.has(signature) || currentPage >= 10000) throw new Error("Invalid NFT inventory pagination");
          seenPages.add(signature);
          currentPage++;
          continue;
        }
        const { page: receivedPage, totalPages, totalCount } = pagination;
        if (receivedPage !== currentPage || !Number.isInteger(totalPages) || totalPages < 0 ||
            !Number.isInteger(totalCount) || totalCount < 0 || totalPages > 10000) {
          throw new Error("Invalid NFT inventory pagination");
        }
        if (expectedTotal !== undefined && totalCount !== expectedTotal) {
          throw new Error("NFT inventory changed during refresh. Please retry.");
        }
        expectedTotal = totalCount;
        if (currentPage >= totalPages) {
          if (Math.max(1, page) === 1 && items.length !== totalCount) {
            throw new Error("Incomplete NFT inventory response");
          }
          break;
        }
        if (!batch.length) throw new Error("Incomplete NFT inventory page");
        currentPage++;
      }
      onSnapshot(snapshot ?? null);
      return items;
}
